import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { PhoneInput } from "react-international-phone";
import {
  countryConstants,
  socialMediaConstants,
  translationConstants,
  fieldLengthConstants,
  Directions,
  Regex,
} from "../../../../../constants";
import { isPhoneValid } from "../../../../../system";
import { allSharedComponents } from "../../../../../shared";
import { authActions, centersActions } from "../../../../../redux/actions";
import { sportNames } from "../../Spots/utilities";

const { InputFieldWrapper } = allSharedComponents;

const GeneralTabForm = ({ isAdmin, centerId }) => {
  const methods = useForm();
  const dispatch = useDispatch();

  const { t } = useTranslation();

  const cities = useSelector((state) => state.LoginReducer.cities);
  const formState = useSelector(
    (state) => state.centersReducer.centerProfileData
  );
  const direction = useSelector(
    (state) => state.LanguageChangeReducer.direction
  );

  const [socialMedia, setSocialMedia] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");

  const [checkedSports, setCheckedSports] = useState(formState?.sportIds);

  const handleCheckboxChange = (event) => {
    const sportId = parseInt(event.target.value);
    if (event.target.checked) {
      setCheckedSports([...checkedSports, sportId]);
    } else {
      setCheckedSports(checkedSports.filter((id) => id !== sportId));
    }
  };

  const {
    control,
    trigger,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = methods;

  const onSubmit = (dataToSend) => {
    dataToSend = {
      ...dataToSend,
      cityId: Number(selectedCity),
      id: centerId,
      centerInfo: {
        ...dataToSend.centerInfo,
        phoneNumber: dataToSend.userInfo.phoneNumber,
        tikTokURL: dataToSend.tiktok ? dataToSend.centerInfo.tikTokURL : "",
        twitterURL: dataToSend.twitter ? dataToSend.centerInfo.twitterURL : "",
        instagramURL: dataToSend.instagram
          ? dataToSend.centerInfo.instagramURL
          : "",
        facebookURL: dataToSend.facebook
          ? dataToSend.centerInfo.facebookURL
          : "",
      },
      userInfo: {
        ...dataToSend.userInfo,
        userEmail: dataToSend.userInfo?.email,
        id: formState?.centerUserInfo[0]?.id,
      },
      sports: checkedSports?.map((id) => id.toString()),
    };
    dispatch(centersActions.updateCenterProfile(dataToSend));
  };

  useEffect(() => {
    dispatch(authActions.fetchCities(countryConstants.SAUDIA_ARABIA));
    if (formState) {
      setValue("userInfo.documents", formState.userInfo?.documents);
      setValue("documentExpiryDate", formState.documentExpiryDate);
      setValue("centerInfo.name", formState.centerInfo.name);
      setValue("centerInfo.arabicName", formState.centerInfo.arabicName);
      setValue("centerInfo.cityId", formState.centerInfo.cityId);
      setValue("centerInfo.address", formState.centerInfo.address);
      setValue("centerInfo.taxNumber", formState.centerInfo.taxNumber);
      setSelectedCity(formState.centerInfo?.cityId);
      setValue("centerInfo.websiteURL", formState.centerInfo.websiteURL);
      setValue("userInfo.phoneNumber", formState.centerInfo.phoneNumber);
      setValue("centerInfo.email", formState.centerInfo?.email);
      setValue("centerInfo.description", formState.centerInfo.description);
      setValue(
        "centerInfo.arabicDescription",
        formState.centerInfo.arabicDescription
      );
      setValue("sports", formState.sports);
      setValue("logo", formState.logo);
      setValue(socialMediaConstants.TWITTER, formState.centerInfo.twitterURL);
      setValue("centerInfo.twitterURL", formState.centerInfo?.twitterURL);
      setValue(
        socialMediaConstants.INSTAGRAM,
        formState.centerInfo.instagramURL
      );
      setValue("centerInfo.instagramURL", formState.centerInfo?.instagramURL);
      setValue(
        socialMediaConstants.FACEBOOK,
        formState.centerInfo?.facebookURL
      );
      setValue("centerInfo.facebookURL", formState.centerInfo?.facebookURL);
      setValue(socialMediaConstants.TIKTOK, formState.centerInfo?.tikTokURL);
      setValue("centerInfo.tikTokURL", formState.centerInfo?.tikTokURL);
      setValue("userInfo.email", formState.centerUserInfo[0]?.email);
      setCheckedSports(formState?.sportIds);
      const socialMediaUrls = {
        [socialMediaConstants.TWITTER]: formState.centerInfo?.twitterURL,
        [socialMediaConstants.FACEBOOK]: formState.centerInfo?.facebookURL,
        [socialMediaConstants.TIKTOK]: formState.centerInfo?.tikTokURL,
        [socialMediaConstants.INSTAGRAM]: formState.centerInfo?.instagramURL,
      };

      const initialSocialMedia = Object.entries(socialMediaUrls)
        .filter(([_, url]) => url)
        .map(([key, _]) => key);

      setSocialMedia(initialSocialMedia);
    }
    // eslint-disable-next-line
  }, [formState]);

  const onSocialMediaChange = (key) => {
    let list = [...socialMedia];
    if (list.includes(key)) list = list.filter((x) => x !== key);
    else list.push(key);

    setSocialMedia(list);
  };

  const atLeastOneSportChecked = () => {
    return checkedSports?.length > 0;
  };

  return (
    <Container>
      <Row>
        <Col>
          <FormProvider {...methods}>
            <Form noValidate className onSubmit={handleSubmit(onSubmit)}>
              <Container>
                <Row>
                  <InputFieldWrapper
                    label={`${t(translationConstants.CENTER_NAME)}:`}
                  >
                    <Form.Control
                      type="text"
                      id="centerInfo.name"
                      maxLength={fieldLengthConstants.TEXT_FIELD_LENGTH}
                      placeholder={t(translationConstants.ENTER_CENTER_NAME)}
                      {...register("centerInfo.name", {
                        required: t(translationConstants.CENTER_NAME_REQUIRED),
                        pattern: {
                          value:
                            /^[A-Za-z0-9\s!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+$/i,
                          message: t(translationConstants.INVALID_CENTER_NAME),
                        },
                      })}
                      disabled={isAdmin}
                    />
                    {errors.centerInfo?.name && (
                      <p className="errorText">
                        {errors.centerInfo?.name.message}
                      </p>
                    )}
                  </InputFieldWrapper>

                  <InputFieldWrapper
                    label={`${t(translationConstants.CENTER_NAME)}: (${t(
                      translationConstants.ARABIC
                    )})`}
                  >
                    <Form.Control
                      {...register("centerInfo.arabicName", {})}
                      id="centerInfo.arabicName"
                      type="text"
                      maxLength={fieldLengthConstants.TEXT_FIELD_LENGTH}
                      disabled={isAdmin}
                      dir={Directions.rtl}
                      placeholder={"أدخل إسم المركز"}
                    />
                    {errors.centerInfo?.arabicName && (
                      <p className="errorText">
                        {errors.centerInfo?.arabicName.message}
                      </p>
                    )}
                  </InputFieldWrapper>

                  <InputFieldWrapper
                    label={`${t(translationConstants.ENTER_CENTER_CITY)}:`}
                  >
                    <Form.Select
                      id="centerInfo.cityId"
                      {...register("centerInfo.cityId", {
                        required: t(translationConstants.CENTER_CITY_REQUIRED),
                      })}
                      disabled={isAdmin}
                      onChange={(e) => {
                        const value = e.target.value;
                        setValue("centerInfo.cityId", value, {
                          shouldValidate: true,
                        });
                        setSelectedCity(value);
                        trigger("centerInfo.cityId");
                        e.stopPropagation();
                      }}
                    >
                      <option value="">
                        {t(translationConstants.selectcity)}
                      </option>
                      {cities?.map((city) => (
                        <option key={city.id} value={city.id}>
                          {direction === Directions.rtl
                            ? city.arabicName
                            : city.name}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.centerInfo?.cityId && (
                      <p className="errorText">
                        {errors.centerInfo.cityId.message}
                      </p>
                    )}
                  </InputFieldWrapper>

                  <InputFieldWrapper
                    label={`${t(translationConstants.CENTER_ADDRESS)}: ${t(
                      translationConstants.PREFERRED_ARABIC
                    )}`}
                  >
                    <Form.Control
                      type="text"
                      disabled={isAdmin}
                      id="centerInfo.address"
                      placeholder={t(translationConstants.ENTER_CENTER_ADDRESS)}
                      {...register("centerInfo.address", {
                        required: t(
                          translationConstants.CENTER_ADDRESS_REQUIRED
                        ),
                      })}
                    />
                    {errors.centerInfo?.address && (
                      <p className="errorText">
                        {errors.centerInfo?.address.message}
                      </p>
                    )}
                  </InputFieldWrapper>

                  <InputFieldWrapper
                    label={`${t(translationConstants.phoneNumber)}:`}
                    className="react-international-button-grey"
                  >
                    <Controller
                      name="userInfo.phoneNumber"
                      control={control}
                      rules={{
                        required: t(translationConstants.phoneNumberRequired),
                        validate: (value) => {
                          const isValid = isPhoneValid(value);
                          if (!isValid) {
                            return t(
                              translationConstants.enterValidPhoneNumber
                            );
                          }
                          return true;
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <PhoneInput
                          disabled={isAdmin}
                          id="phone-number-registration"
                          defaultCountry="sa"
                          value={value}
                          onChange={onChange}
                          maxLength={
                            fieldLengthConstants.PHONENUMBER_FIELD_LENGTH
                          }
                        />
                      )}
                    />
                    {errors.userInfo?.phoneNumber && (
                      <p className="errorText">
                        {errors.userInfo?.phoneNumber.message}
                      </p>
                    )}
                  </InputFieldWrapper>

                  <InputFieldWrapper
                    label={`${t(translationConstants.CENTER_TAX_NUMBER)}:`}
                  >
                    <Form.Control
                      type="text"
                      disabled={isAdmin}
                      id="centerInfo.taxNumber"
                      placeholder={t(
                        translationConstants.ENTER_CENTER_TAX_NUMBER
                      )}
                      {...register("centerInfo.taxNumber", {
                        required: t(
                          translationConstants.CENTER_TAX_NUMBER_REQUIRED
                        ),
                        pattern: {
                          value: Regex.fifteenDigitsOnlyRegex,
                          message: t(
                            translationConstants.CENTER_TAX_NUMBER_NUMERIC_ONLY
                          ),
                        },
                      })}
                    />
                    {errors.centerInfo?.taxNumber && (
                      <p className="errorText">
                        {errors.centerInfo?.taxNumber.message}
                      </p>
                    )}
                  </InputFieldWrapper>

                  <InputFieldWrapper
                    label={`${t(translationConstants.WEBSITE_LINK)}:`}
                  >
                    <Form.Control
                      type="text"
                      id="centerInfo.websiteURL"
                      placeholder={t(translationConstants.ENTER_WEBSITE_LINK)}
                      {...register("centerInfo.websiteURL", {
                        pattern: {
                          value: Regex.urlRegex,
                          message: t(translationConstants.URL_VALIDATION),
                        },
                      })}
                      disabled={isAdmin}
                    />
                    {errors.centerInfo?.websiteURL && (
                      <p className="errorText">
                        {errors.centerInfo?.websiteURL.message}
                      </p>
                    )}
                  </InputFieldWrapper>

                  <InputFieldWrapper
                    label={`${t(translationConstants.CONTACT_EMAIL)}:`}
                  >
                    <Form.Control
                      {...register("centerInfo.email", {
                        required: t(translationConstants.emailRequired),
                        pattern: {
                          value:
                            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,}$/i,
                          message: t(translationConstants.invalidEmail),
                        },
                      })}
                      disabled={isAdmin}
                      id="centerInfo.email"
                      type="centerInfo.email"
                      placeholder="Ex: abc@example.com"
                    />
                    {errors.centerInfo?.email && (
                      <p className="errorText">
                        {errors.centerInfo?.email.message}
                      </p>
                    )}
                  </InputFieldWrapper>

                  <InputFieldWrapper
                    md={12}
                    label={`${t(translationConstants.CENTER_DESCRIPTION)}:`}
                  >
                    <Form.Control
                      type="text"
                      id="centerInfo.description"
                      as="textarea"
                      rows={2}
                      placeholder={t(
                        translationConstants.ENTER_CENTER_DESCRIPTION
                      )}
                      {...register("centerInfo.description", {})}
                      disabled={isAdmin}
                    />
                    {errors.centerInfo?.description && (
                      <p className="errorText">
                        {errors.centerInfo?.description.message}
                      </p>
                    )}
                  </InputFieldWrapper>

                  <InputFieldWrapper
                    md={12}
                    label={`${t(translationConstants.CENTER_DESCRIPTION)}: ${t(
                      translationConstants.ARABIC
                    )}`}
                  >
                    <Form.Control
                      type="text"
                      id="centerInfo.arabicDescription"
                      as="textarea"
                      rows={2}
                      placeholder={t(
                        translationConstants.ENTER_CENTER_DESCRIPTION
                      )}
                      {...register("centerInfo.arabicDescription", {})}
                      disabled={isAdmin}
                    />
                    {errors.centerInfo?.arabicDescription && (
                      <p className="errorText">
                        {errors.centerInfo?.arabicDescription.message}
                      </p>
                    )}
                  </InputFieldWrapper>

                  <Col md={8} sm={12} className="mb-3">
                    <Form.Label className="Form-labels">
                      {t(translationConstants.CHOOSE_SPORTS_YOU_OFFER)}:
                    </Form.Label>
                    <div className="d-flex justify-content-between col-md-12 px-0">
                      {Object.entries(sportNames)
                        .filter(
                          ([sportId]) =>
                            !isAdmin ||
                            checkedSports?.includes(parseInt(sportId))
                        )
                        .map(([sportId, sportName]) => (
                          <Form.Group
                            key={sportId}
                            className="d-flex flex-column justify-content-space-between"
                            controlId={`formBasicCheckbox${sportId}`}
                          >
                            <Form.Check
                              className="offer"
                              type="checkbox"
                              value={sportId}
                              disabled={isAdmin}
                              label={
                                <p>
                                  {t(
                                    translationConstants[
                                      sportName.toLowerCase()
                                    ]
                                  )}
                                </p>
                              }
                              {...register("sports", {
                                validate: atLeastOneSportChecked,
                              })}
                              checked={checkedSports?.includes(
                                parseInt(sportId)
                              )}
                              onChange={handleCheckboxChange}
                            />
                          </Form.Group>
                        ))}
                    </div>

                    {errors.sports && (
                      <p className="errorText">{t("atleast_one_sport")}</p>
                    )}
                  </Col>

                  <Col md={12} className="mb-3">
                    <Form.Label className="Form-labels">
                      {t(translationConstants.SOCIAL_MEDIA_LINKS)}:
                    </Form.Label>
                    <Row>
                      <Col
                        md={6}
                        sm={12}
                        className={
                          isAdmin && !formState?.centerInfo?.twitterURL
                            ? "d-none"
                            : ""
                        }
                      >
                        <Form.Group
                          className="social-media-links"
                          controlId="socialMediaTwitterCheckboxes"
                        >
                          <Form.Check
                            type="checkbox"
                            className="custom-checkbox custom-align"
                            {...register(socialMediaConstants.TWITTER)}
                            disabled={isAdmin}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              onSocialMediaChange(socialMediaConstants.TWITTER);
                              //Trigger validation for the Twitter URL input field
                              trigger("centerInfo.twitterURL").then(() => {
                                //If checkbox is unchecked, clear the error
                                if (!isChecked) {
                                  clearErrors("centerInfo.twitterURL");
                                  setValue("centerInfo.twitterURL", null);
                                }
                              });
                            }}
                            label={
                              <Row>
                                <Col
                                  xs={3}
                                  className="d-flex align-items-center"
                                >
                                  <span>{t(translationConstants.TWITTER)}</span>
                                </Col>
                                <Col xs={9}>
                                  <Form.Control
                                    type="text"
                                    id="centerInfo.twitterURL"
                                    disabled={
                                      !socialMedia.includes(
                                        socialMediaConstants.TWITTER
                                      ) || isAdmin
                                    }
                                    {...register("centerInfo.twitterURL", {
                                      validate: {
                                        validateTwitter: (field, formValues) =>
                                          !(formValues.twitter && !field) ||
                                          t(translationConstants.URL_REQUIRED),
                                      },
                                      pattern: {
                                        value: Regex.urlRegex,
                                        message: t(
                                          translationConstants.URL_VALIDATION
                                        ),
                                      },
                                    })}
                                  />
                                </Col>
                                {errors.centerInfo?.twitterURL && (
                                  <p className="errorText ml-82 d-md-none d-flex d-lg-flex">
                                    {errors.centerInfo.twitterURL.message}
                                  </p>
                                )}
                                {errors.centerInfo?.twitterURL && (
                                  <p className="errorText ml-55 d-none d-md-flex d-lg-none">
                                    {errors.centerInfo.twitterURL.message}
                                  </p>
                                )}
                              </Row>
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col
                        md={6}
                        sm={12}
                        className={
                          isAdmin && !formState?.centerInfo?.instagramURL
                            ? "d-none"
                            : ""
                        }
                      >
                        <Form.Group
                          className="social-media-links"
                          controlId="socialMediaInstagramCheckboxes"
                        >
                          <Form.Check
                            type="checkbox"
                            className="custom-checkbox custom-align"
                            {...register(socialMediaConstants.INSTAGRAM)}
                            disabled={isAdmin}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              onSocialMediaChange(
                                socialMediaConstants.INSTAGRAM
                              );
                              trigger("centerInfo.instagramURL").then(() => {
                                if (!isChecked) {
                                  clearErrors("centerInfo.instagramURL");
                                  setValue("centerInfo.instagramURL", null);
                                }
                              });
                            }}
                            label={
                              <Row>
                                <Col
                                  xs={3}
                                  className="d-flex align-items-center"
                                >
                                  <span>
                                    {t(translationConstants.INSTAGRAM)}
                                  </span>
                                </Col>
                                <Col>
                                  <Form.Control
                                    type="text"
                                    id="centerInfo.instagramURL"
                                    disabled={
                                      !socialMedia.includes(
                                        socialMediaConstants.INSTAGRAM
                                      ) || isAdmin
                                    }
                                    {...register("centerInfo.instagramURL", {
                                      validate: {
                                        validateInstagram: (
                                          field,
                                          formValues
                                        ) =>
                                          !(formValues.instagram && !field) ||
                                          t(translationConstants.URL_REQUIRED),
                                      },
                                      pattern: {
                                        value: Regex.urlRegex,
                                        message: t(
                                          translationConstants.URL_VALIDATION
                                        ),
                                      },
                                    })}
                                  />
                                </Col>
                                {errors.centerInfo?.instagramURL && (
                                  <p className="errorText ml-82 d-md-none d-flex d-lg-flex">
                                    {errors.centerInfo?.instagramURL.message}
                                  </p>
                                )}
                                {errors.centerInfo?.instagramURL && (
                                  <p className="errorText ml-55 d-none d-md-flex d-lg-none">
                                    {errors.centerInfo?.instagramURL.message}
                                  </p>
                                )}
                              </Row>
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col
                        md={6}
                        sm={12}
                        className={
                          isAdmin && !formState?.centerInfo?.facebookURL
                            ? "d-none"
                            : ""
                        }
                      >
                        <Form.Group
                          className="social-media-links"
                          controlId="socialMediaFacebookCheckboxes"
                        >
                          <Form.Check
                            type="checkbox"
                            className="custom-checkbox custom-align"
                            {...register(socialMediaConstants.FACEBOOK)}
                            disabled={isAdmin}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              onSocialMediaChange(
                                socialMediaConstants.FACEBOOK
                              );
                              trigger("centerInfo.facebookURL").then(() => {
                                if (!isChecked) {
                                  clearErrors("centerInfo.facebookURL");
                                  setValue("centerInfo.facebookURL", null);
                                }
                              });
                            }}
                            label={
                              <Row>
                                <Col
                                  xs={3}
                                  className="d-flex align-items-center"
                                >
                                  <span>
                                    {t(translationConstants.FACEBOOK)}
                                  </span>
                                </Col>
                                <Col>
                                  <Form.Control
                                    type="text"
                                    id="centerInfo.facebookURL"
                                    disabled={
                                      !socialMedia.includes(
                                        socialMediaConstants.FACEBOOK
                                      ) || isAdmin
                                    }
                                    {...register("centerInfo.facebookURL", {
                                      validate: {
                                        validateFacebook: (field, formValues) =>
                                          !(formValues.facebook && !field) ||
                                          t(translationConstants.URL_REQUIRED),
                                      },
                                      pattern: {
                                        value: Regex.urlRegex,
                                        message: t(
                                          translationConstants.URL_VALIDATION
                                        ),
                                      },
                                    })}
                                  />
                                </Col>
                                {errors.centerInfo?.facebookURL && (
                                  <p className="errorText ml-82 d-md-none d-flex d-lg-flex">
                                    {errors.centerInfo?.facebookURL.message}
                                  </p>
                                )}
                                {errors.centerInfo?.facebookURL && (
                                  <p className="errorText ml-55 d-none d-md-flex d-lg-none">
                                    {errors.centerInfo?.facebookURL.message}
                                  </p>
                                )}
                              </Row>
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col
                        md={6}
                        sm={12}
                        className={
                          isAdmin && !formState?.centerInfo?.tikTokURL
                            ? "d-none"
                            : ""
                        }
                      >
                        <Form.Group
                          className="social-media-links"
                          controlId="socialMediaTiktokCheckboxes"
                        >
                          <Form.Check
                            type="checkbox"
                            className="custom-checkbox custom-align"
                            disabled={isAdmin}
                            {...register(socialMediaConstants.TIKTOK)}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              onSocialMediaChange(socialMediaConstants.TIKTOK);
                              trigger("centerInfo.tikTokURL").then(() => {
                                if (!isChecked) {
                                  clearErrors("centerInfo.tikTokURL");
                                  setValue("centerInfo.tikTokURL", null);
                                }
                              });
                            }}
                            label={
                              <Row>
                                <Col
                                  xs={3}
                                  className="d-flex align-items-center"
                                >
                                  <span>{t(translationConstants.TIKTOK)}</span>
                                </Col>
                                <Col>
                                  <Form.Control
                                    type="text"
                                    id="centerInfo.tikTokURL"
                                    disabled={
                                      !socialMedia.includes(
                                        socialMediaConstants.TIKTOK
                                      ) || isAdmin
                                    }
                                    {...register("centerInfo.tikTokURL", {
                                      validate: {
                                        validateTiktok: (field, formValues) =>
                                          !(formValues.tiktok && !field) ||
                                          t(translationConstants.URL_REQUIRED),
                                      },
                                      pattern: {
                                        value: Regex.urlRegex,
                                        message: t(
                                          translationConstants.URL_VALIDATION
                                        ),
                                      },
                                    })}
                                  />
                                </Col>
                                {errors.centerInfo?.tikTokURL && (
                                  <p className="errorText ml-82 d-md-none d-flex d-lg-flex">
                                    {errors.centerInfo.tikTokURL.message}
                                  </p>
                                )}
                                {errors.centerInfo?.tikTokURL && (
                                  <p className="errorText ml-55 d-none d-md-flex d-lg-none">
                                    {errors.centerInfo.tikTokURL.message}
                                  </p>
                                )}
                              </Row>
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Col>

                  <div>
                    <Col md={12} className="mb-4">
                      <p className="font-weight-700 font-16 text-dark">
                        {t(translationConstants.LOGIN_DETAILS_HEADING)}
                      </p>
                      <hr className="horizontal-divider m-0" />
                    </Col>

                    <InputFieldWrapper
                      label={`${t(translationConstants.Email)}`}
                    >
                      <Form.Control
                        type="text"
                        id="userInfo.email"
                        maxLength={fieldLengthConstants.EMAIL_FIELD_LENGTH}
                        disabled={isAdmin}
                        placeholder={t(translationConstants.emailPlaceholder)}
                        {...register("userInfo.email", {
                          required: t(translationConstants.emailRequired),
                          pattern: {
                            value:
                              /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,}$/i,
                            message: t(translationConstants.invalidEmail),
                          },
                        })}
                      />
                      {errors.userInfo?.email && (
                        <p className="errorText">
                          {errors.userInfo.email.message}
                        </p>
                      )}
                    </InputFieldWrapper>
                  </div>
                </Row>

                {!isAdmin && (
                  <Row>
                    <Col md={12} className="d-flex justify-content-end">
                      <div className="d-flex">
                        <Button className="profile-button" type="submit">
                          {t(translationConstants.UPDATE_PROFILE)}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                )}
              </Container>
            </Form>
          </FormProvider>
        </Col>
      </Row>
    </Container>
  );
};

export default GeneralTabForm;
